/*
 * TODO(image-pipeline): the `ImageData` 5-tuple is NOT consumed as sm/md/lg/xl/xxl.
 *
 * `getImageSourcePairs` (below) uses `sizeOrder = [MD, LG, XL, XXL]` (only 4 entries)
 * and loops `i < sizeOrder.length`, and `getMediaQuery` gives the first <source> no
 * min-width. So for any entry `[d0, d1, d2, d3, d4]` the real <picture> output is:
 *   d0 -> width < 1024 (sm AND md)     <- also the base <img> src
 *   d1 -> 1024 – 1279.98  (lg)
 *   d2 -> 1280 – 1439.98  (xl)
 *   d3 -> >= 1440         (xxl)
 *   d4 -> NEVER READ (dead in every entry)
 * Breakpoints: sm 500 / md 768 / lg 1024 / xl 1280 / xxl 1440.
 *
 * So the `// sm .. // xxl` labels on `ImageDataDb` entries describe intent, not behaviour.
 * Fixing the tuple alters the <picture> output for every image in the app, so it needs its
 * own PR and a visual regression pass. See docs/adr/0003.
 */
/*
 * TODO(image-pipeline): no `sizes` attribute is emitted, so every `w` descriptor resolves
 * against the default `100vw` and the browser takes the largest candidate on any desktop
 * viewport regardless of DPR. The fix is a `sizes` input on `sirius-image-item`, threaded
 * into the <img> and every <source>. See docs/adr/0003.
 */
import { Injectable } from '@angular/core';
import { DeviceData, DeviceSize } from '../../../_shared/data/device.data';
import {
    IImageService,
    IImageServiceConfig,
    ImageDimensions,
    ImageSource,
    ImageSourcePair,
} from '../../../_shared/models';

import type { ImageProvider } from '../../../_shared/types/image';

const MAX_WIDTH = 3840;
const MAX_HEIGHT = 3840;

export abstract class ImageServiceBase {
    constructor(protected readonly config: IImageServiceConfig) {}

    // TODO:Remove support for `dims` in next major release (v2.0.0)
    // - Make `dimensions` required
    // - Remove `resolvedDimensions()`
    // - Remove `get dim()` and `get br()` from `ImageSourcePair`
    protected get resolvedDimensions(): ImageDimensions[] {
        const resolved = this.config.dimensions ?? this.config.dims;

        if (!resolved) {
            console.warn('[ImageService] No dimensions provided in config.');
            return [];
        }

        if (!this.config.dimensions && this.config.dims) {
            console.warn('[ImageService] `dims` is deprecated. Please use `dimensions` instead.');
        }

        return resolved;
    }

    protected getImageSourcePairs(dimensions: ImageDimensions[]): ImageSourcePair[] {
        const sizeOrder: DeviceSize[] = [DeviceSize.MD, DeviceSize.LG, DeviceSize.XL, DeviceSize.XXL];

        const pairs: ImageSourcePair[] = [];

        for (let i = 0; i < dimensions.length && i < sizeOrder.length; i++) {
            const current = dimensions[i];
            const previous = dimensions[i - 1];
            const size = sizeOrder[i];

            const isFirst = i === 0;
            const isDifferent = isFirst || current.w !== previous.w || current.h !== previous.h;

            if (isDifferent) {
                const breakpoint = DeviceData.breakpoints[size];
                pairs.push(new ImageSourcePair(current, breakpoint));
            }
        }

        return pairs;
    }

    /**
     * Sources tile on the same min-width breakpoints as `@sirius/responsive`
     * (`_exclusive-max`, `-0.02px`). A `+1`/max-width scheme would mismatch
     * breakpoint-driven container styles by 1px right on the boundary.
     */
    protected getMediaQuery(breakpoints: number[], index: number): string {
        const minWidth = index > 0 ? `(min-width: ${breakpoints[index]}px)` : '';
        const maxWidth = index < breakpoints.length - 1 ? `(max-width: ${breakpoints[index + 1] - 0.02}px)` : '';

        return [minWidth, maxWidth].filter(Boolean).join(' and ');
    }

    protected getRetinaSrcSet(dimensions: ImageDimensions): string {
        const baseUrl = this.config.url;
        const focal = this.focalFilterSuffix;
        const standard = `${dimensions.w}x${dimensions.h}${focal} ${dimensions.w}w`;
        const retina = `${dimensions.w * 2}x${dimensions.h * 2}${focal} ${dimensions.w * 2}w`;

        return [baseUrl + standard, baseUrl + retina].join(', ');
    }

    /**
     * Storyblok focal-point filter appended after the `WxH` crop, e.g.
     * `/filters:focal(661x386:662x387)`. Empty when the service is not cropping,
     * since `fit-in` resizes without cropping and the filter would do nothing.
     */
    protected get focalFilterSuffix(): string {
        const focusPoint = this.config.focusPoint;

        if (!focusPoint || this.config.crop === false) {
            return '';
        }

        return `/filters:focal(${focusPoint})`;
    }
}

export class ImageServiceStoryblok extends ImageServiceBase implements IImageService {
    constructor(config: IImageServiceConfig) {
        super(config);

        this.config.crop = config.crop ?? true;

        this.config.url += '/m/' + (this.config.crop ? '' : 'fit-in/');
    }

    getSrc(): string {
        const dimensions = this.resolvedDimensions;

        if (!dimensions.length) {
            // crop:false already routed config.url through /m/fit-in/ in the
            // constructor — appending fit-in again would 404 on the image service.
            const fitIn = this.config.crop === false ? '' : 'fit-in/';
            return this.config.url + `${fitIn}${MAX_WIDTH}x${MAX_HEIGHT}`;
        }

        const dimension = dimensions[0];
        return this.config.url + `${dimension.w}x${dimension.h}` + this.focalFilterSuffix;
    }

    getSrcSet(): string {
        const dimensions = this.resolvedDimensions;
        if (!dimensions.length) return '';

        return this.getRetinaSrcSet(dimensions[0]);
    }

    getPictureSources(): ImageSource[] {
        const dimensions = this.resolvedDimensions;
        const pairs = this.getImageSourcePairs(dimensions);
        const breakpoints = pairs.map((p) => p.breakpoint);

        return pairs.map((pair, index) => {
            const media = this.getMediaQuery(breakpoints, index);
            const srcSet = this.getRetinaSrcSet(pair.dimensions);
            return new ImageSource(media, srcSet);
        });
    }
}

@Injectable({
    providedIn: 'root',
})
export class ImageSourceService {
    getImageService(type: ImageProvider, config: IImageServiceConfig): IImageService | undefined {
        switch (type) {
            case 'storyblok':
                return new ImageServiceStoryblok(config);
            case 'unknown':
            default:
                return undefined;
        }
    }
}
